/**
 * Admin → District CMS.
 *
 * One reusable editor for every area page (Train Factory and any future
 * district): pick the area, then edit its banner, tagline, sections,
 * listings and ordering with the same tools.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, MapPinned } from "lucide-react";

import { listDistrictsForAdmin } from "@/lib/districts.functions";
import { countPendingTrainFactoryListings } from "@/lib/train-factory-users.functions";
import { TrainFactoryMapTab } from "@/components/admin/business/TrainFactoryMapTab";
import { TfPendingMark } from "@/components/admin/TfPendingMark";

export const Route = createFileRoute("/_authenticated/admin/districts")({
  component: AdminDistrictsPage,
  head: () => ({
    meta: [
      { title: "District CMS · What Finland admin" },
      { name: "description", content: "Edit area pages: banner, sections, listings and order." },
      { property: "og:title", content: "District CMS · What Finland admin" },
      { property: "og:description", content: "Edit area pages: banner, sections, listings and order." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function AdminDistrictsPage() {
  const listDistricts = useServerFn(listDistrictsForAdmin);
  const [slug, setSlug] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "districts", "list"],
    queryFn: () => listDistricts(),
    staleTime: 60_000,
  });
  const pendingTfFn = useServerFn(countPendingTrainFactoryListings);
  const pendingTfQuery = useQuery({
    queryKey: ["admin", "tf-pending-count"],
    queryFn: () => pendingTfFn(),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const pendingTfListings = pendingTfQuery.data?.count ?? 0;

  useEffect(() => {
    if (!slug && data && data.length > 0) {
      const preferred = data.find((d) => d.slug === "train-factory") ?? data[0];
      setSlug(preferred.slug);
    }
  }, [data, slug]);

  const active = data?.find((d) => d.slug === slug) ?? null;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
      <Link
        to="/admin/$page"
        params={{ page: "dashboard" }}
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#495057] hover:text-[#212529]"
      >
        <ArrowLeft className="size-4" />
        Back to admin
      </Link>

      <header className="mt-3 mb-5">
        <h1 className="font-heading text-2xl text-[#212529] sm:text-3xl">District CMS</h1>
        <p className="mt-1 text-sm text-[#6c757d]">
          Choose an area, then edit its banner, sections, listings and order. Every area page uses the same layout.
        </p>
        <Link
          to="/admin/train-factory-users"
          className="mt-3 inline-flex text-sm font-semibold text-[#0d6efd] hover:underline"
        >
          Invite Train Factory users
        </Link>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-[#6c757d]">
          <Loader2 className="size-4 animate-spin" />
          Loading areas…
        </div>
      ) : error ? (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error instanceof Error ? error.message : "Could not load areas."}
        </p>
      ) : (data?.length ?? 0) === 0 ? (
        <p className="rounded-xl bg-slate-50 px-4 py-8 text-center text-sm text-[#6c757d]">No areas yet.</p>
      ) : (
        <>
          <div className="mb-5 flex flex-wrap gap-2">
            {(data ?? []).map((d) => {
              const on = d.slug === slug;
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => setSlug(d.slug)}
                  className={`inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-[13px] font-semibold transition ${
                    on ? "bg-[#0b1d3a] text-white" : "border border-[#dee2e6] bg-white text-[#212529] hover:bg-slate-50"
                  }`}
                >
                  <MapPinned className="size-3.5" />
                  {d.name}
                  {d.slug === "train-factory" && pendingTfListings > 0 ? <TfPendingMark /> : null}
                  {!d.published ? (
                    <span className={`text-[11px] font-semibold ${on ? "text-white/70" : "text-[#adb5bd]"}`}>
                      · draft
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {active ? (
            <TrainFactoryMapTab key={active.slug} districtSlug={active.slug} districtName={active.name} />
          ) : null}
        </>
      )}
    </div>
  );
}
